import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  Save, Search, Tag, Package, Box, Settings, List,
  Trash2, ArrowLeft, Info, Plus, X, ChevronRight, Paperclip, ChevronLeft,
  History as HistoryIcon, ShieldCheck, User, Calendar, GitBranch, RefreshCw, Type, Minus
} from 'lucide-react';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next'; // 🎯 الترجمة

// استيراد المودالز
import UniversalLookup from './UniversalLookup';
import StatusModal from './StatusModal';
import StatusHistoryModal from './StatusHistoryModal';
import AttachmentsModal from './AttachmentsModal';
import AuditHistoryModal from './AuditHistoryModal';
import SmartPrintButton from './PrintButton';
import AddToolToStoreModal from './AddToolToStoreModal';
import ClassificationTree from './ClassificationTree';
import { usePermissions } from './usePermissions';
import CustomFieldSlot from './CustomFieldSlot';
import { useFontSize } from './FontSizeContext'; // 🎯 التحكم في الخط

const ToolDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !id || id === 'new';

  const { t, i18n } = useTranslation();
  const isRTL = i18n.language.startsWith('ar') || i18n.language.startsWith('ur');
  const fontContext = useFontSize();
  const fontSize = fontContext?.fontSize || 13;
  const { canCreate, canUpdate, canDelete } = usePermissions('tools');

  const [activeTab, setActiveTab] = useState('TOOL');
  const [fieldMaps, setFieldMaps] = useState({});
  const [loading, setLoading] = useState(false);
  const [count, setCount] = useState(0);
  const [selectedSpecIdx, setSelectedSpecIdx] = useState(null);
  const [showClassTree, setShowClassTree] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [isDocModalOpen, setIsDocModalOpen] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [isAddStoreModalOpen, setIsAddStoreModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const [data, setData] = useState({
    toolnum: '', description: '', status: 'ACTIVE', tooltype: 'HAND',
    is_stocked: false, rotating: true, capitalized: false,
    orderunit: 'EACH', issueunit: 'EACH', calibration_required: false,
    standard_rate: 0,
    inventory: [], specifications: [], qualifications: [], vendors: []
  });

  const [nav, setNav] = useState({ current_pos: 0, total: 0, next: null, prev: null, first: null, last: null });
  const [lookup, setLookup] = useState({ isOpen: false, title: '', type: '', domainId: '', field: '', filters: {} });

  // --- التحكم في الفونت ---
  const FontControls = () => (
    <div className="flex items-center gap-2 bg-surface-card/10 px-3 py-1 rounded-full border border-white/20 ml-4">
      <button onClick={() => fontContext.changeFontSize(-1)} className="hover:text-status-warning transition-colors"><Minus size={14} /></button>
      <div className="flex items-center gap-1 min-w-[40px] justify-center text-white/70">
        <Type size={14} /><span className="font-black text-[10px]">{fontSize}</span>
      </div>
      <button onClick={() => fontContext.changeFontSize(1)} className="hover:text-status-warning transition-colors"><Plus size={14} /></button>
    </div>
  );

  const loadData = useCallback(async () => {
    try {
        const [assetMaps, meterMaps] = await Promise.all([
            axios.get('/api/admin/field-map/tools'),
            axios.get('/api/admin/field-map/Meters')
        ]);
        setFieldMaps({ ...(assetMaps.data || {}), ...(meterMaps.data || {}) });
    }
    catch (err) { toast.error(t('common.load_error', "Error loading field maps")); }

    if (isNew) return;

    setLoading(true);
    try {
      const res = await axios.get(`/api/tools/${id}`);
      setData(res.data);

      // جلب الملحقات
      const countRes = await axios.get(`/api/attachments/count/TOOL/${id}`);
      setCount(countRes.data.count || 0);
    } catch (err) { toast.error(t('common.load_error', "Error loading tool")); }
    finally { setLoading(false); }
  }, [id, isNew, t]);

  useEffect(() => { loadData(); }, [loadData]);

  // 🎯 جلب النافيجيشن بناءً على الكويري
  const loadNavigation = useCallback(async () => {
    if (isNew) return;
    try {
      const navRes = await axios.get(`/api/tools/${id}/navigation${location.search}`);
      setNav(navRes.data);
    } catch (err) { console.error("Nav Load Failed"); }
  }, [id, isNew, location.search]);

  useEffect(() => { loadNavigation(); }, [loadNavigation]);

  useEffect(() => {
    const openLog = () => setShowAudit(true);
    window.addEventListener('GLOBAL_LOG_HANDLER', openLog);
    return () => window.removeEventListener('GLOBAL_LOG_HANDLER', openLog);
  }, []);

  const goTo = (targetId) => targetId && navigate(`/tools/${targetId}${location.search}`);

  const handleClassSelect = async (node) => {
    try {
      const loadId = toast.loading(t('common.loading', "Fetching attributes..."));
      const res = await axios.get(`/api/classifications/${node.classstructureid}/attributes`);

      const newSpecs = res.data.map(attr => ({
        assetattrid: attr.assetattrid, description: attr.description, datatype: attr.datatype,
        uom: attr.uom, domainid: attr.domainid, alnvalue: '', numvalue: null
      }));

      setData(prev => ({
        ...prev, classstructureid: node.classstructureid, classstructureid_description: node.description, specifications: newSpecs
      }));

      toast.success(t('tools.messages.class_loaded', "Classification Loaded"), { id: loadId });
      setShowClassTree(false);
    } catch (err) { toast.error(t('common.error', "Failed to load attributes")); }
  };

  const handleSave = async () => {
    if (!data.toolnum || !data.description) return toast.error(t('tools.messages.req_fields', "Tool Number and Description are required!"));
    const lid = toast.loading(t('common.saving', "Saving Tool Master..."));
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/tools/save', data, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success(t('common.save_success', "Tool Saved Successfully"), { id: lid });
      if (isNew) navigate(`/tools/${data.toolnum}${location.search}`);
    } catch (err) { toast.error(t('common.save_fail', "Save Failed"), { id: lid }); }
  };

  const handleDelete = async () => {
    const result = await Swal.fire({
      title: t('common.are_you_sure', 'Are you sure?'),
      text: t('tools.messages.delete_warn', "You won't be able to revert this!"),
      icon: 'warning', showCancelButton: true, confirmButtonColor: '#d33',
      confirmButtonText: t('common.yes_delete', 'Yes, delete tool!'),
      customClass: { popup: `${isRTL ? 'font-cairo' : 'font-sans'} text-[12px]` }
    });
    if (result.isConfirmed) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`/api/tools/${id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        toast.success(t('common.delete_success', "Tool Deleted"));
        navigate(`/tools${location.search}`);
      } catch (err) { toast.error(t('common.delete_fail', "Delete Failed")); }
    }
  };

  const handleSmartSearch = (fieldName, title, defaultType) => {
    const linkedDomain = fieldMaps[fieldName];
    let extraFilters = {};
    if (fieldName === 'siteid' && data.orgid) extraFilters = { org_id: data.orgid };

    setLookup({
        isOpen: true, title: title, type: linkedDomain ? 'DOMAIN' : defaultType,
        domainId: linkedDomain || '', field: fieldName, filters: extraFilters
    });
  };

  useEffect(() => {
    const openStatusModal = () => { setShowStatusModal(true); };
    window.addEventListener('GLOBAL_STATUS_HANDLER', openStatusModal);
    return () => window.removeEventListener('GLOBAL_STATUS_HANDLER', openStatusModal);
  }, []);

  useEffect(() => {
    const AddModalOpen = () => { setIsAddModalOpen(true); };
    window.addEventListener('OPEN_ADD_ITEM_STOREROOM', AddModalOpen);
    return () => window.removeEventListener('OPEN_ADD_ITEM_STOREROOM', AddModalOpen);
  }, []);

  useEffect(() => {
    const triggerSave = () => handleSave();
    window.addEventListener('GLOBAL_SAVE_HANDLER', triggerSave);
    return () => window.removeEventListener('GLOBAL_SAVE_HANDLER', triggerSave);
  }, [handleSave]);

  useEffect(() => {
    const triggerDelete = () => handleDelete();
    window.addEventListener('GLOBAL_DELETE_HANDLER', triggerDelete);
    return () => window.removeEventListener('GLOBAL_DELETE_HANDLER', triggerDelete);
  }, [handleDelete]);

  useEffect(() => {
    const handleOpenHistory = () => { setShowHistoryModal(true); };
    window.addEventListener('OPEN_STATUS_HISTORY', handleOpenHistory);
    return () => { window.removeEventListener('OPEN_STATUS_HISTORY', handleOpenHistory); };
  }, []);

  const handleInputChange = (f, v) => setData(prev => ({ ...prev, [f]: v }));

  const addNewSpecification = () => {
    const newSpec = { assetattrid: '', description: '', datatype: 'ALN', alnvalue: '', numvalue: null, uom: '', domainid: null };
    setData({ ...data, specifications: [...(data.specifications || []), newSpec] });
    setSelectedSpecIdx((data.specifications || []).length);
  };

  // 🎯 الاختيار لازم يروح لسطر المواصفة المحدد - الـ onSelect العام كان بيحط
  // القيمة في data.assetattrid (هيدر الأداة) فالمواصفة كانت بتفضل فاضية
  const openSpecAttributeLookup = (specIdx) => {
    setLookup({
      isOpen: true, title: t('tools.lookup.attr', 'Attributes'), type: 'attributes', domainId: '', field: 'assetattrid', filters: {},
      onCustomSelect: (row) => setData(prev => {
        const specs = [...(prev.specifications || [])];
        specs[specIdx] = { ...specs[specIdx], assetattrid: row.value, description: row.description || specs[specIdx].description };
        return { ...prev, specifications: specs };
      }),
    });
  };

  const handleSpecUpdate = (field, value) => {
    if (selectedSpecIdx === null) return;
    const newList = [...data.specifications];
    newList[selectedSpecIdx][field] = value;
    setData({ ...data, specifications: newList });
  };

  if (loading) return <div className="p-20 text-center animate-pulse font-black text-primary">{t('common.loading', 'Loading...')}</div>;

  return (
    <div className={`flex flex-col h-screen bg-surface-app uppercase tracking-tighter overflow-hidden duration-300 transition-[font-size] ${isRTL ? 'font-cairo text-right' : 'text-left'}`} style={{ fontSize: `${fontSize}px` }} dir={isRTL ? 'rtl' : 'ltr'}>

      {/* --- TOP TOOLBAR --- */}
      <div className="bg-primary-dark text-white p-1.5 flex justify-between items-center shadow-lg z-30 px-4 shrink-0">
        <div className={`flex items-center gap-3 ${isRTL ? 'mr-2' : 'ml-2'}`}>
          <button onClick={() => navigate(`/tools${location.search}`)} className="p-1.5 hover:bg-white/10 rounded-full text-white/70 transition-colors">
            {isRTL ? <ChevronRight size={18} /> : <ArrowLeft size={18}/>}
          </button>

          {!isNew && (
            <div className={`flex items-center bg-black/20 border border-white/10 rounded-md px-1 py-0.5 gap-1 `}>
              <button disabled={!nav.prev} onClick={() => goTo(nav.first)} className="p-1 hover:text-status-warning disabled:opacity-30"><ChevronLeft size={16} strokeWidth={3} className={isRTL ? 'rotate-180' : ''}/></button>
              <button disabled={!nav.prev} onClick={() => goTo(nav.prev)} className="p-1 hover:text-status-warning disabled:opacity-30"><ChevronLeft size={16} className={isRTL ? 'rotate-180' : ''}/></button>
              <div className="px-2 text-[10px] font-mono border-x border-white/10 mx-1"><span className="text-status-warning font-black">{nav.current_pos}</span> / {nav.total}</div>
              <button disabled={!nav.next} onClick={() => goTo(nav.next)} className="p-1 hover:text-status-warning disabled:opacity-30"><ChevronRight size={16} className={isRTL ? 'rotate-180' : ''}/></button>
              <button disabled={!nav.next} onClick={() => goTo(nav.last)} className="p-1 hover:text-status-warning disabled:opacity-30"><ChevronRight size={16} strokeWidth={3} className={isRTL ? 'rotate-180' : ''}/></button>
            </div>
          )}
        </div>

        <div className={`flex items-center gap-2 ${isRTL ? 'ml-2' : 'mr-2'}`}>
          {((isNew && canCreate) || (!isNew && canUpdate)) && (
            <button onClick={handleSave} className="bg-status-success hover:bg-status-success text-white px-6 py-1.5 rounded-sm flex items-center gap-2 font-black transition-all shadow-md">
              <Save size={14} /> {t('common.save', 'SAVE')}
            </button>
          )}

          <div className="h-6 w-[1px] bg-white/10 mx-1"></div>

          {!isNew && <SmartPrintButton moduleName="TOOL" refId={data.toolnum} />}
          {!isNew && (
            <button onClick={() => setShowStatusModal(true)} className="p-2 hover:bg-white/10 rounded text-white/70" title={t('common.change_status')}><Tag size={16}/></button>
          )}
          {!isNew && (
            <button onClick={() => setIsDocModalOpen(true)} className="relative p-2 hover:bg-white/10 rounded text-white/70">
              <Paperclip size={16} />
              {count > 0 && <span className="absolute top-1 right-1 bg-status-danger text-[8px] w-3.5 h-3.5 rounded-full flex items-center justify-center font-black animate-pulse">{count}</span>}
            </button>
          )}
          {!isNew && (
            <button onClick={() => setShowHistoryModal(true)} className="p-2 hover:bg-white/10 rounded text-white/70"><HistoryIcon size={16}/></button>
          )}
          {!isNew && canDelete && (
            <button onClick={handleDelete} className="p-2 hover:bg-status-danger/20 rounded text-status-danger"><Trash2 size={16}/></button>
          )}
          <FontControls />
        </div>
      </div>

      {/* --- HEADER INFO --- */}
      <div className="bg-surface-card border-b px-6 py-3 flex items-center gap-12 shadow-sm z-20">
        <div className="flex flex-col min-w-[150px]">
            <label className="text-[9px] text-ink-secondary font-black uppercase tracking-widest">{t('tools.fields.toolnum', 'Tool Number')}</label>
            <input
              className={`text-sm font-black text-primary-dark outline-none border-b-2 border-transparent focus:border-status-warning bg-transparent transition-all ${isRTL ? 'text-right' : ''}`}
              value={data.toolnum}
              placeholder={t('tools.placeholders.toolid', 'ENTER TOOL ID...')}
              disabled={!isNew}
              onChange={e => handleInputChange('toolnum', e.target.value.toUpperCase())}
            />
        </div>
        <div className={`flex flex-col flex-1 border-line ${isRTL ? 'border-r pr-8' : 'border-l pl-8'}`}>
          <label className="text-[9px] text-ink-secondary font-black uppercase tracking-widest">{t('assets.headers.desc', 'Description')}</label>
          <input className={`text-sm font-bold text-ink outline-none w-full bg-transparent focus:text-primary border-b border-transparent focus:border-primary transition-all ${isRTL ? 'text-right' : ''}`} value={data.description} onChange={e => handleInputChange('description', e.target.value)} />
        </div>
        <div className={`flex flex-col border-line ${isRTL ? 'border-r pr-8 text-right' : 'border-l pl-8'}`}>
          <label className="text-[9px] text-ink-secondary font-black uppercase tracking-widest">{t('assets.headers.status', 'Status')}</label>
          <span className={`px-3 py-1 rounded-full font-black text-[10px] w-fit mt-1 ${data.status === 'ACTIVE' ? 'bg-status-success-bg text-status-success' : 'bg-status-danger-bg text-status-danger'}`}>{data.status}</span>
        </div>
      </div>

      {/* --- TABS --- */}
      <div className="flex bg-primary-dark text-white shadow-md px-4 overflow-x-auto no-scrollbar">
        {[
          { id: 'TOOL', label: t('tools.tabs.tool', 'TOOL'), icon: <Settings size={14}/> },
          { id: 'STORES', label: t('tools.tabs.stores', 'STORES'), icon: <Package size={14}/> },
          { id: 'VENDORS', label: t('tools.tabs.vendors', 'VENDORS'), icon: <Box size={14}/> },
          { id: 'SPECS', label: t('tools.tabs.specs', 'SPECS'), icon: <List size={14}/> },
          { id: 'QUALS', label: t('tools.tabs.quals', 'QUALS'), icon: <ShieldCheck size={14}/> }
        ].map(tb => (
          <button key={tb.id} onClick={() => setActiveTab(tb.id)}
            className={`px-8 py-3 flex items-center gap-2 font-black border-white/5 transition-all whitespace-nowrap ${isRTL ? 'border-l' : 'border-r'} ${activeTab === tb.id ? 'bg-surface-card text-primary-dark' : 'hover:bg-white/10 opacity-75'}`}>
            {tb.icon} {tb.label}
          </button>
        ))}
      </div>

      {/* --- CONTENT --- */}
      <div className="flex-1 overflow-y-auto p-8 custom-scrollbar bg-surface-panel">
        {activeTab === 'TOOL' && (
          <div className="max-w-6xl mx-auto space-y-6 animate-in fade-in duration-500">
            <SectionCard title={t('tools.sections.config', 'Tool Configuration')}>
               <div className="grid grid-cols-2 gap-20">
                  <div className="space-y-5">
                    <EditableField label={t('tools.fields.type', 'Tool Type')} value={data.tooltype} onChange={v => handleInputChange('tooltype', v)} onSearch={() => {handleSmartSearch('tooltype', t('tools.lookup.type', 'Tool Type'), 'DOMAIN')}} isRTL={isRTL} />
                    <DataField label={t('tools.fields.issueunit', 'Issue Unit')} value={data.issueunit} onSearch={() => {handleSmartSearch('issueunit', t('tools.lookup.unit', 'Issue Units'), 'DOMAIN')}} isRTL={isRTL} />
                    <EditableField label={t('tools.fields.standard_rate', 'Standard Rate / Hour')} value={data.standard_rate} onChange={v => handleInputChange('standard_rate', v)} isRTL={isRTL} />
                    <div className="pt-4 space-y-3 border-t border-line-subtle">
                      <CheckboxField label={t('tools.fields.is_stocked', 'Is Stocked Tool?')} checked={data.is_stocked} onChange={v => handleInputChange('is_stocked', v)} color="status-warning" isRTL={isRTL} />
                      <CheckboxField label={t('tools.fields.rotating', 'Rotating Item?')} checked={data.rotating} onChange={v => handleInputChange('rotating', v)} isRTL={isRTL} />
                      <CheckboxField label={t('tools.fields.calib_req', 'Calibration Required?')} checked={data.calibration_required} onChange={v => handleInputChange('calibration_required', v)} isRTL={isRTL} />
                    </div>
                    <CustomFieldSlot coreModelName="TOOL" tab="TOOL" section="Configuration" recordId={data.toolnum} disabled={isNew} />
                  </div>
                  <div className={`space-y-5 border-line-subtle ${isRTL ? 'border-r pr-10' : 'border-l pl-10'}`}>
                     <StaticField label={t('tools.fields.mfg', 'Manufacturer')} value={data.manufacturer} />
                     <StaticField label={t('tools.fields.model', 'Model Num')} value={data.model_num} />
                     <div className={`bg-status-warning-bg p-6 border-status-warning rounded-sm shadow-inner italic font-bold text-status-warning leading-relaxed uppercase mt-6 ${isRTL ? 'border-r-4' : 'border-l-4'}`}>
                        {t('tools.messages.stocked_note', 'Note: Stocked tools are managed through storerooms and require an inventory record to track balances.')}
                     </div>
                  </div>
               </div>
            </SectionCard>
          </div>
        )}

        {activeTab === 'STORES' && (
          <div className="max-w-6xl mx-auto bg-surface-card border shadow-xl rounded-sm overflow-hidden animate-in slide-in-from-right">
             <div className="bg-surface-panel p-3 border-b flex justify-between px-8 items-center">
                <span className="font-black text-primary tracking-widest italic text-[10px]">{t('tools.sections.inventory', 'Inventory & Stock Balances')}</span>
                <button onClick={() => setIsAddModalOpen(true)} className="bg-primary text-white px-4 py-1.5 rounded-sm font-black text-[9px] hover:bg-primary-hover shadow-md flex items-center gap-2 transition-transform active:scale-95">
                   <Plus size={12}/> {t('tools.actions.add_store', 'Add to Storeroom')}
                </button>
             </div>
             <InventoryTable inventory={data.inventory} isRTL={isRTL} t={t} />
          </div>
        )}
        {activeTab === 'VENDORS' && (
            <div className="bg-surface-card border shadow-2xl max-w-6xl mx-auto rounded-sm overflow-hidden animate-in slide-in-from-right duration-500">
                <VendorsTable vendors={data.vendors} isRTL={isRTL} t={t} />
            </div>
        )}
        {activeTab === "SPECS" && (
            <div className="max-w-7xl mx-auto space-y-4 animate-in slide-in-from-bottom-4 duration-500">
            <div className="bg-surface-card border p-4 shadow-sm rounded-sm flex flex-col md:flex-row justify-between items-end gap-4">
              <div className="flex-1 w-full"><EditableField label={t('tools.fields.class', 'Classification')} value={data.classstructureid_description || data.classstructureid} onSearch={() => setShowClassTree(true)} isRTL={isRTL} /></div>
              <button onClick={addNewSpecification} className="bg-primary text-white px-6 py-1.5 rounded-sm font-bold shadow hover:bg-primary-dark transition-all text-xs flex items-center gap-2 active:scale-95"><Plus size={14} /> {t('common.new_row', 'New Row')}</button>
            </div>
            <div className="bg-surface-card border rounded shadow-sm overflow-hidden min-h-[400px]">
               <SpecsTable specs={data.specifications} selectedIdx={selectedSpecIdx} onSelect={setSelectedSpecIdx} isRTL={isRTL} t={t} />
               {selectedSpecIdx !== null && data.specifications?.[selectedSpecIdx] && (
                  <div className={`p-6 border-t bg-surface-panel grid grid-cols-2 gap-10 ${isRTL ? 'text-right' : 'text-left'}`}>
                      <EditableField label={t('tools.tables.attr', 'Attribute')} value={data.specifications[selectedSpecIdx].assetattrid} onSearch={() => openSpecAttributeLookup(selectedSpecIdx)} isRTL={isRTL} />
                      <div className="flex flex-col gap-1 border-b border-line">
                          <label className="text-ink-secondary font-bold text-[9px] uppercase">{t('tools.tables.value', 'Value')}</label>
                          <input className={`outline-none py-1 bg-transparent font-bold text-primary ${isRTL ? 'text-right' : 'text-left'}`} value={data.specifications[selectedSpecIdx].alnvalue || data.specifications[selectedSpecIdx].numvalue || ''} onChange={e => handleSpecUpdate('alnvalue', e.target.value)} />
                      </div>
                  </div>
               )}
            </div>
          </div>
        )}

        {activeTab === 'QUALS' && (
          <div className="max-w-6xl mx-auto bg-surface-card border shadow-xl rounded-sm overflow-hidden animate-in slide-in-from-bottom">
             <div className="bg-surface-panel p-3 border-b flex justify-between px-8 items-center border-t-4 border-t-status-warning">
                <span className="font-black text-status-warning tracking-widest italic text-[0.9em]">{t('tools.sections.quals', 'Required Qualifications')}</span>
                <button className="bg-status-warning text-white px-4 py-1.5 rounded-sm font-black text-[0.9em] hover:bg-status-warning flex items-center gap-2 active:scale-95">
                   <Plus size={12}/> {t('tools.actions.add_qual', 'Add Qualification')}
                </button>
             </div>
             <table className={`w-full font-bold uppercase ${isRTL ? 'text-right' : 'text-left'}`}>
                <thead className="bg-surface-panel border-b text-ink-secondary text-[0.9em]">
                  <tr>
                    <th className={`p-4 border-line-subtle ${isRTL ? 'border-l' : 'border-r'}`}>{t('tools.tables.qual', 'Qualification')}</th>
                    <th className={`p-4 border-line-subtle ${isRTL ? 'border-l' : 'border-r'}`}>{t('assets.headers.desc', 'Description')}</th>
                    <th className="p-4">{t('common.org', 'Org ID')}</th>
                  </tr>
                </thead>
                <tbody className="text-[0.9em]">
                  {data.qualifications?.length > 0 ? data.qualifications.map((q, i) => (
                    <tr key={i} className="border-b hover:bg-status-warning-bg transition-colors">
                      <td className={`p-4 border-line-subtle text-primary ${isRTL ? 'border-l' : 'border-r'}`}>{q.qualification}</td>
                      <td className={`p-4 border-line-subtle text-ink-secondary ${isRTL ? 'border-l' : 'border-r'}`}>{q.description}</td>
                      <td className="p-4">{q.orgid}</td>
                    </tr>
                  )) : <tr><td colSpan="3" className="p-20 text-center text-ink-secondary font-black italic uppercase tracking-widest">{t('common.no_records', 'No rows to display')}</td></tr>}
                </tbody>
             </table>
          </div>
        )}
      </div>

      {/* --- MODALS --- */}
      <UniversalLookup
        {...lookup}
        onClose={() => setLookup({...lookup, isOpen: false})}
        onSelect={(row) => {
          if (lookup.onCustomSelect) { lookup.onCustomSelect(row); }
          else {
            const val = row.assetnum || row.location_id || row.itemnum || row.meter_num || row.value || row.id || row.site_id;
            handleInputChange(lookup.field, val);
          }
          setLookup(prev => ({ ...prev, isOpen: false }));
        }}
      />
      {isAddModalOpen && (
      <AddToolToStoreModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        toolnum={data.toolnum}
        onRefresh={() => {
            axios.get(`/api/tools/${data.toolnum}`).then(res => setData(res.data));
        }}
      />)}
      {showStatusModal && (
      <StatusModal isOpen={showStatusModal} currentStatus={data.status} recordId={data.toolnum} appName="TOOL" onClose={() => setShowStatusModal(false)} onComplete={loadData} />)}
      {isDocModalOpen && (
      <AttachmentsModal isOpen={isDocModalOpen} ownertable="TOOL" ownerid={data.toolnum} onClose={() => { setIsDocModalOpen(false); fetchCount(); }} />)}
      <AuditHistoryModal isOpen={showAudit} onClose={() => setShowAudit(false)} tableName="tools" recordId={data.toolnum} />
      {showHistoryModal && (
      <StatusHistoryModal isOpen={showHistoryModal} appName="TOOL" recordId={data.toolnum} onClose={() => setShowHistoryModal(false)} />)}
      {showClassTree && (
      <ClassificationTree isOpen={showClassTree} onClose={() => setShowClassTree(false)} onSelect={handleClassSelect} />)}
    </div>
  );
};

// --- HELPERS ---
const SectionCard = ({ title, children }) => (
  <div className="bg-surface-card border rounded shadow-sm overflow-hidden"><div className="p-2 bg-surface-panel border-b font-black text-ink-secondary uppercase text-[0.9em] tracking-widest flex items-center gap-2 shadow-inner"><div className="w-1.5 h-3 bg-primary rounded-full"></div> {title}</div><div className="p-8">{children}</div></div>
);

const StaticField = ({ label, value }) => (
  <div className="flex flex-col gap-1 border-b border-line-subtle pb-1 mb-4"><label className="text-ink-secondary font-black text-[0.9em] uppercase tracking-widest">{label}</label><div className="font-bold text-ink min-h-[15px]">{value || '---'}</div></div>
);

const CheckboxField = ({ label, checked, onChange, color="primary", isRTL }) => (
  <label className={`flex items-center gap-3 cursor-pointer group py-1 `}>
    <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} className={`w-4 h-4 accent-${color} rounded-sm cursor-pointer`} />
    <span className={`group-hover:text-${color} transition-colors font-black text-ink-secondary text-[0.9em] uppercase`}>{label}</span>
  </label>
);

const EditableField = ({ label, value, onChange, onSearch, isRTL }) => (
  <div className={`flex flex-col gap-1 border-b border-line focus-within:border-primary transition-all uppercase mb-4 ${isRTL ? 'text-right' : 'text-left'}`}>
    <label className="text-ink-secondary font-black text-[0.9em] tracking-widest">{label}</label>
    <div className={`flex items-center justify-between py-1 `}>
        <input className={`font-black text-primary outline-none bg-transparent flex-1 text-[0.9em] ${isRTL ? 'text-right' : 'text-left'}`} value={value || ''} onChange={e => onChange(e.target.value)} />
        {onSearch && <Search size={14} className="text-primary cursor-pointer" onClick={onSearch} />}
    </div>
  </div>
);

const DataField = ({ label, value, onSearch, isRTL }) => (
  <div className={`flex flex-col border-b border-line-subtle py-1.5 group hover:bg-primary-soft transition-all rounded px-1 mb-4 ${isRTL ? 'text-right' : 'text-left'}`}>
      <label className="text-[0.9em] font-black text-ink-secondary uppercase">{label}</label>
      <div className={`flex justify-between items-center h-5 `}>
          <span className="font-black uppercase text-primary">{value || '---'}</span>
          {onSearch && <Search size={13} className="text-primary cursor-pointer" onClick={onSearch} />}
      </div>
  </div>
);

const InventoryTable = ({ inventory = [], isRTL, t }) => (
  <table className={`w-full font-bold uppercase tracking-tighter ${isRTL ? 'text-right' : 'text-left'}`}>
    <thead className="bg-surface-panel border-b text-ink-secondary text-[0.9em]">
        <tr>
            <th className={`p-4 border-line-subtle ${isRTL ? 'border-l' : 'border-r'}`}>{t('tools.tables.storeroom', 'Storeroom')}</th>
            <th className={`p-4 border-line-subtle ${isRTL ? 'border-l' : 'border-r'}`}>{t('common.site', 'Site')}</th>
            <th className={`p-4 border-line-subtle ${isRTL ? 'border-l text-left' : 'border-r text-right'}`}>{t('tools.tables.balance', 'Balance')}</th>
            <th className="p-4 text-center">{t('assets.headers.status', 'Status')}</th>
        </tr>
    </thead>
    <tbody className="text-[0.9em]">
      {inventory.length > 0 ? inventory.map((item, idx) => (
        <tr key={idx} className="border-b hover:bg-primary-soft transition-colors font-black">
            <td className={`p-4 border-line-subtle text-primary ${isRTL ? 'border-l' : 'border-r'}`}>{item.location}</td>
            <td className={`p-4 border-line-subtle text-ink-secondary ${isRTL ? 'border-l' : 'border-r'}`}>{item.siteid}</td>
            <td className={`p-4 border-line-subtle text-status-success font-mono ${isRTL ? 'border-l text-left' : 'border-r text-right'}`}>{parseFloat(item.curbal || 0).toFixed(2)}</td>
            <td className="p-4 text-center">ACTIVE</td>
        </tr>
      )) : <tr><td colSpan="4" className="p-20 text-center text-ink-secondary font-black italic uppercase tracking-widest">{t('tools.messages.no_stock', 'No items in stock')}</td></tr>}
    </tbody>
  </table>
);

const SpecsTable = ({ specs = [], selectedIdx, onSelect, isRTL, t }) => (
    <table className={`w-full border-collapse ${isRTL ? 'text-right' : 'text-left'}`}>
        <thead className="bg-surface-panel border-b text-ink-secondary font-bold text-[0.9em] uppercase">
            <tr>
                <th className="p-3 w-10"></th>
                <th className={`p-3 border-line ${isRTL ? 'border-l' : 'border-r'}`}>{t('tools.tables.attr', 'Attribute')}</th>
                <th className={`p-3 border-line ${isRTL ? 'border-l' : 'border-r'}`}>{t('assets.headers.desc', 'Description')}</th>
                <th className={`p-3 border-line ${isRTL ? 'border-l' : 'border-r'}`}>{t('tools.tables.value', 'Value')}</th>
                <th className="p-3">{t('tools.tables.uom', 'UOM')}</th>
            </tr>
        </thead>
        <tbody>
            {specs.map((spec, i) => (
                <tr key={i} onClick={() => onSelect(i)} className={`border-b hover:bg-primary-soft cursor-pointer text-[11px] ${selectedIdx === i ? 'bg-primary-soft' : ''}`}>
                    <td className="p-3 text-center text-primary">{selectedIdx === i && <ChevronRight size={14} className={isRTL ? 'rotate-180 mx-auto' : 'mx-auto'}/>}</td>
                    <td className={`p-3 border-line-subtle font-bold text-primary ${isRTL ? 'border-l' : 'border-r'}`}>{spec.assetattrid || '---'}</td>
                    <td className={`p-3 border-line-subtle text-ink-secondary ${isRTL ? 'border-l' : 'border-r'}`}>{spec.description || '---'}</td>
                    <td className={`p-3 border-line-subtle font-bold text-status-success ${isRTL ? 'border-l' : 'border-r'}`}>{spec.alnvalue || spec.numvalue || '---'}</td>
                    <td className="p-3 text-ink-secondary">{spec.uom || '---'}</td>
                </tr>
            ))}
        </tbody>
    </table>
);

const VendorsTable = ({ vendors = [], isRTL, t }) => (
    <div className="flex flex-col animate-in fade-in duration-500">
        <div className={`bg-surface-panel p-3 border-b border-t-4 border-t-status-success flex justify-between items-center px-8 shadow-sm `}>
            <span className={`text-[0.9em] font-black text-status-success tracking-widest italic flex items-center gap-2 `}>
                <Box size={14} className="text-status-success"/> {t('tools.sections.vendors', 'PROCUREMENT & SUPPLIER HISTORY')}
            </span>
            <span className="bg-status-success-bg text-status-success px-3 py-1 rounded-full text-[0.8em] font-black border border-status-success-bg">
                {t('tools.fields.active_vendors', 'ACTIVE VENDORS')}: {vendors.length}
            </span>
        </div>
        <table className={`w-full border-collapse font-bold uppercase tracking-tighter ${isRTL ? 'text-right' : 'text-left'}`}>
            <thead className="bg-surface-panel border-b text-ink-secondary text-[0.9em]">
                <tr>
                    <th className={`p-4 border-line w-48 ${isRTL ? 'border-l' : 'border-r'}`}>{t('tools.tables.vendor_id', 'Vendor (Comp Num)')}</th>
                    <th className={`p-4 border-line ${isRTL ? 'border-l' : 'border-r'}`}>{t('tools.tables.vendor_name', 'Supplier Name')}</th>
                    <th className={`p-4 border-line text-center w-32 ${isRTL ? 'border-l' : 'border-r'}`}>{t('tools.tables.unitcost', 'Unit Cost')}</th>
                    <th className={`p-4 border-line text-center w-24 ${isRTL ? 'border-l' : 'border-r'}`}>{t('tools.tables.qty', 'Qty')}</th>
                    <th className="p-4 w-32 text-center">{t('tools.tables.po', 'Reference PO')}</th>
                </tr>
            </thead>
            <tbody className="text-[0.9em]">
                {vendors.length > 0 ? vendors.map((v, i) => {
                    const cost = v.unitcost && !isNaN(v.unitcost) ? parseFloat(v.unitcost) : 0;
                    return (
                        <tr key={i} className="border-b hover:bg-status-success-bg transition-colors group">
                            <td className={`p-4 border-line-subtle text-primary font-black ${isRTL ? 'border-l' : 'border-r'}`}>
                                {v.vendor_code || v.vendor || '---'}
                            </td>
                            <td className={`p-4 border-line-subtle text-ink-secondary italic ${isRTL ? 'border-l' : 'border-r'}`}>
                                {v.vendor_name || t('tools.placeholders.reg_supplier', 'REGISTERED SUPPLIER')}
                            </td>
                            <td className={`p-4 border-line-subtle text-center font-mono text-status-success ${isRTL ? 'border-l' : 'border-r'}`}>
                                {cost.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                <span className={`text-[0.8em] text-ink-secondary font-sans ${isRTL ? 'mr-1' : 'ml-1'}`}>{v.currency || 'EGP'}</span>
                            </td>
                            <td className={`p-4 border-line-subtle text-center font-mono text-ink ${isRTL ? 'border-l' : 'border-r'}`}>
                                {v.quantity || 0}
                            </td>
                            <td className="p-4 text-ink-secondary font-medium text-center">
                                {v.ponum || t('tools.placeholders.adj', 'STOCK ADJ')}
                            </td>
                        </tr>
                    );
                }) : (
                    <tr>
                        <td colSpan="5" className="p-20 text-center text-ink-secondary font-black italic tracking-widest">
                            {t('common.no_records', 'No Procurement Records Found')}
                        </td>
                    </tr>
                )}
            </tbody>
        </table>
    </div>
);

export default ToolDetails;
